import IconBadge from 'components/icon-badge/IconBadge'
import CustomUsageForm from 'features/usage-form/custom-usage-form/CustomUsageForm'
import { useMemo, useState } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import { InputGroupKeys } from 'store/customer/enums'
import { EstimatedUsage } from 'types/usages'
import useWindowSize from 'hooks/useWindowSize'
import { useCheckUsage } from 'hooks/useCheckUsage'
import { UsageStepFormInputs } from 'features/simulation/steps/types'
import { MAX_SOLAR_KVA } from 'constants/usages'
import { UsageFormProps } from './types'
import { checkHasInjectionUsageFields, checkHasReversingCounter, checkHasSolarKva } from 'features/simulation/utils'
import { useStoreSelector } from 'hooks/store'
import { Banner, Button, Form, InlineMessage, List, Toggle } from '@boltenergy-be/design-system'
import SubmitButton from 'features/simulation/components/submit-button/SubmitButton'
import { REGIONS, USAGE_GRADES, UsageGrade, Usage, COUNTER_TYPES, CUSTOMER_TYPES, ESTIMATED_USAGES } from '@boltenergy-be/shared'
import { useRouter } from 'next/router'
import { routes } from 'utils/routes'
import { formatAmount } from 'utils/format'

const UsageForm = ({ onBlur, onSubmit, hasEnergyTypeToggle, warning }: UsageFormProps) => {
  // Redux
  const { inputs } = useStoreSelector((store) => store.customer)
  const { isCompany } = inputs[InputGroupKeys.PERSONAL_DATA]
  const { region } = inputs[InputGroupKeys.SIMULATION]

  // React Hook Form
  const hookForm = useFormContext<UsageStepFormInputs>()
  const {
    formState: { isValid },
    setValue,
    watch
  } = hookForm

  // Router
  const { asPath, locale } = useRouter()

  // React Hook Form watch
  const watchCounterType = watch('counterType')
  const watchElectricity = watch('electricity')
  const watchGas = watch('gas')
  const watchHasSolarPanels = watch('hasSolarPanels')
  const watchSolarKva = watch('solarKva')
  const watchUsageGrade = watch('usageGrade')
  const watchNeedsGas = watch('needsGas')

  // React memo's
  const watchUsage = useMemo<Usage>(
    () => ({
      electricity: watchElectricity,
      gas: watchGas,
      grade: watchUsageGrade,
      solarKva: watchSolarKva
    }),
    [watchElectricity, watchGas]
  )
  const hasKva = useMemo<boolean>(
    () => checkHasSolarKva({ counterType: watchCounterType, hasSolarPanels: watchHasSolarPanels }),
    [watchCounterType, watchHasSolarPanels]
  )
  const hasInjection = useMemo<boolean>(
    () => checkHasInjectionUsageFields({ counterType: watchCounterType, hasSolarPanels: watchHasSolarPanels }),
    [watchCounterType, watchHasSolarPanels]
  )
  const hasReversingCounter = useMemo<boolean>(
    () => checkHasReversingCounter({ counterType: watchCounterType, hasSolarPanels: watchHasSolarPanels }),
    [watchCounterType, watchHasSolarPanels]
  )

  // Local state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [previousUsageGrade, setPreviousUsageGrade] = useState<UsageGrade>()

  // Window size
  const { isTablet } = useWindowSize()

  // Custom hooks
  const { isValidUsage } = useCheckUsage({
    hasExclusiveNightMeter: inputs.simulation.hasExclusiveNightMeter,
    meterType: inputs.simulation.meterType,
    usage: { ...watchUsage }
  })

  // i18n
  const { t, ready } = useTranslation(['simulation', 'common'])
  if (!ready) return null

  // Constants
  const clientType = !isCompany ? CUSTOMER_TYPES.residential : CUSTOMER_TYPES.professional
  const estimatedUsage: EstimatedUsage =
    watchUsageGrade !== USAGE_GRADES.custom
      ? ESTIMATED_USAGES[watchUsageGrade][clientType]
      : previousUsageGrade
        ? ESTIMATED_USAGES[previousUsageGrade][clientType]
        : undefined

  /**
   * Handles the usage grade switch
   *
   * @param {UsageGrade} grade
   */
  const handleUsageGradeSwitch = (grade: UsageGrade) => {
    if (watchUsageGrade !== USAGE_GRADES.custom) {
      setPreviousUsageGrade(watchUsageGrade)
    }

    setDefaultValues()
    setValue('usageGrade', grade)
  }

  /**
   * Handles the form submit
   */
  const handleSubmit = async () => {
    setIsSubmitting(true)
    await onSubmit()
    setIsSubmitting(false)
  }

  /**
   * Sets the default values for electriciy, gas & solarKva
   */
  const setDefaultValues = () => {
    setValue('electricity', {
      consumption: {
        single: estimatedUsage?.electricity.consumption,
        double: {
          day: estimatedUsage?.electricity.consumption,
          night: estimatedUsage?.electricity.consumption
        }
      },
      injection: {
        single: estimatedUsage?.electricity.injection,
        double: {
          day: estimatedUsage?.electricity.injection,
          night: estimatedUsage?.electricity.injection
        }
      }
    })
    setValue('gas', estimatedUsage?.gas)
    setValue('solarKva', estimatedUsage?.electricity.kva)
  }

  return (
    <>
      {hasEnergyTypeToggle && (
        <Form.Fieldset title={t('simulation:steps.energyType.title')} className="grid-col-full">
          <Toggle
            className="mt-200"
            isFullwidth
            active={!!watchNeedsGas}
            onClick={(value) => setValue('needsGas', value)}
            options={[
              { label: t('common:electricity'), value: false },
              { label: t('common:electricityAndGas'), value: true }
            ]}
          />
        </Form.Fieldset>
      )}

      <Form.Fieldset
        title={t('simulation:steps.usage.title')}
        titleClassName={routes(locale).simulation.includes(asPath) ? 'h3 serif mb-400' : undefined}
      >
        <Toggle
          className="mt-200"
          active={watchUsageGrade}
          isFullwidth
          onClick={handleUsageGradeSwitch}
          options={[
            { label: t('simulation:steps.usage.options.low.name'), value: USAGE_GRADES.low },
            { label: t('simulation:steps.usage.options.average.name'), value: USAGE_GRADES.average },
            { label: t('simulation:steps.usage.options.high.name'), value: USAGE_GRADES.high },
            { label: t('simulation:steps.usage.options.custom.name'), value: USAGE_GRADES.custom }
          ]}
        />
      </Form.Fieldset>

      {watchUsageGrade !== USAGE_GRADES.custom ? (
        <>
          <div className="flex-col gap-300">
            <p>
              {t('simulation:steps.usage.description-intro', {
                usage: t(`simulation:steps.usage.options.${watchUsageGrade.toLowerCase()}.name`).toLowerCase()
              })}
            </p>

            <List as="ul">
              {(
                t(`simulation:steps.usage.options.${watchUsageGrade.toLowerCase()}.${isCompany ? 'company' : 'residential'}`, {
                  returnObjects: true
                }) as string[]
              )?.map((item, key) => (
                <li key={`${watchUsageGrade}-${key}`}>{item}</li>
              ))}
            </List>
          </div>

          <div className="flex-row gap-200">
            <strong>{t('simulation:steps.usage.estimatedUsage.title')}</strong>
            <Button representation="link" onClick={() => handleUsageGradeSwitch(USAGE_GRADES.custom)}>
              {t('common:change')}
            </Button>
          </div>

          <div className="flex-row gap-500">
            <IconBadge
              tooltipContent={t('simulation:steps.usage.form.electricity')}
              label={`${hasReversingCounter ? 0 : formatAmount(estimatedUsage.electricity.consumption)} kWh`}
              icon="electricity"
            />
            {watchNeedsGas && (
              <IconBadge
                tooltipContent={t('simulation:steps.usage.form.gas')}
                label={`${formatAmount(estimatedUsage.gas)} kWh`}
                icon="gas"
              />
            )}

            {watchHasSolarPanels ? (
              <>
                {hasInjection && (
                  <IconBadge
                    tooltipContent={t('simulation:steps.usage.form.injection')}
                    label={`${estimatedUsage.electricity.injection} kWh`}
                    icon="solarPanel"
                    actionButton={{
                      icon: 'cross',
                      onClick: () => setValue('hasSolarPanels', false)
                    }}
                  />
                )}

                {hasKva && (
                  <IconBadge
                    tooltipContent={t('simulation:steps.usage.form.solarKva')}
                    label={`${estimatedUsage.electricity.kva} kVA`}
                    icon="solarPanel"
                    actionButton={{
                      icon: 'cross',
                      onClick: () => setValue('hasSolarPanels', false)
                    }}
                  />
                )}
              </>
            ) : (
              <Button
                onClick={() => {
                  if (region === REGIONS.brussels && watchCounterType === COUNTER_TYPES.analog) {
                    setValue('counterType', COUNTER_TYPES.digital)
                  }
                  setValue('hasSolarPanels', true)
                }}
                size="small"
                variant="secondary"
                leadingIcon="plus"
              >
                {t('simulation:steps.usage.hasSolarPanels')}
              </Button>
            )}
          </div>
        </>
      ) : (
        <FormProvider {...hookForm}>
          <CustomUsageForm needsGas={watchNeedsGas} {...{ onBlur }} />

          {!isValidUsage && (
            <InlineMessage className="grid-col-full" type="negative">
              {t('simulation:steps.usage.alerts.onlyResidentialUsage')}
            </InlineMessage>
          )}
          {watchSolarKva > MAX_SOLAR_KVA && (
            <InlineMessage className="grid-col-full" type="negative">
              {t('simulation:steps.usage.alerts.moreThan10kva')}
            </InlineMessage>
          )}
        </FormProvider>
      )}

      {!!warning && (
        <Banner className="mt-auto" type="informative">
          {warning}
        </Banner>
      )}

      <SubmitButton
        type="button"
        loading={isSubmitting}
        disabled={!isValidUsage || (watchUsageGrade === USAGE_GRADES.custom && !isValid) || watchSolarKva > MAX_SOLAR_KVA}
        onClick={handleSubmit}
      >
        {isTablet ? t('common:next') : t('simulation:steps.usage.showPersonalProposition')}
      </SubmitButton>
    </>
  )
}

export default UsageForm
